import { Box, Dialog, Grid, Link, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';

import { Button } from '@components/Form';
import exit from '@assets/exitIcon.svg';
import { getLinkToken } from '@api/backendAPI';
import { getLocalizedContent as l } from '@helpers/languages';
import { formatPriceWithCents } from '@utils/currency';
import { createPadPayment } from '@api/backendAPI';
import content from './PaymentAmountConfirmationModal.json';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'SchnyderSLight',
    textAlign: 'center',
  },
}));

export const PaymentAmountConfirmationModal = ({
  open,
  onClose,
  orderId,
  amountCharged,
  onTokenSuccess,
  paymentError,
  onTryAgainError,
  hasPendingACHPayments,
}) => {
  const [confirmPayment, setConfirmPayment] = useState(true);
  const [tokenError, setTokenError] = useState(false);
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';
  const classes = useStyles();
  const amountChargedString = formatPriceWithCents(
    amountCharged.amount,
    amountCharged.currency
  );
  const prevACHPayment = hasPendingACHPayments?.paymentAmount || 0;
  const processingAmountString = formatPriceWithCents(
    prevACHPayment,
    amountCharged.currency,
    language
  );
  const searchParams = new URLSearchParams(window.location.search);
  const paymentProcessor =
    searchParams.get('processor') ||
    process.env.REACT_APP_STRIPE_PAYMENT_METHOD;

  return (
    <Dialog open={open} onClose={onClose}>
      {/* 'X' button */}
      <Grid container direction="row" justifyContent="flex-end">
        <Box
          paddingTop={2}
          paddingRight={2}
          onClick={onClose}
          style={{ cursor: 'pointer' }}
        >
          <img src={exit} alt="exit" />
        </Box>
      </Grid>

      <Box paddingY={2} paddingX={2}>
        <Grid container spacing={4}>
          {/* Title */}
          <Grid item xs={12} container justifyContent="center">
            <Typography variant="h4" className={classes.title}>
              {prevACHPayment > 0
                ? l(content.processingAmountHeader, language)
                : l(content.payAmount, language)}
            </Typography>
          </Grid>

          {/*Processing Amount */}
          {prevACHPayment > 0 && (
            <Grid
              item
              xs={12}
              container
              justifyContent="center"
              style={{ marginBottom: '-20px' }}
            >
              <Typography>
                {l(content.processingAmount, language)}
                {processingAmountString}
              </Typography>
            </Grid>
          )}

          {/* Amount */}
          <Grid item xs={12} container justifyContent="center">
            <Typography>
              {l(content.amountCharged, language)}
              {amountChargedString}
            </Typography>
          </Grid>

          {/* Error */}
          {(paymentError || tokenError) && (
            <Grid item xs={12} container justifyContent="center">
              <Typography color="error">
                {l(content.genericError, language)}
              </Typography>
            </Grid>
          )}
          {/* Confirm button */}
          <Grid item xs={12} container justifyContent="center">
            {tokenError ? (
              <TryAgainButton
                tryAgain={() => {
                  setTokenError(false);
                  setConfirmPayment(false);
                }}
              />
            ) : paymentError ? (
              <TryAgainButton
                tryAgain={() => {
                  onTryAgainError();
                  setConfirmPayment(false);
                }}
              />
            ) : confirmPayment ? (
              <ConfirmPaymentButton
                paymentConfirmed={() => setConfirmPayment(false)}
                isDuplicateACH={prevACHPayment > 0}
              />
            ) : (
              <TokenLoaderChooser
                orderId={orderId}
                amount={amountCharged}
                onTokenSuccess={onTokenSuccess}
                onTokenFailure={() => setTokenError(true)}
                paymentProcessor={paymentProcessor}
              />
            )}
          </Grid>

          {/* Cancel button */}
          <Grid item xs={12} container justifyContent="center">
            <Box paddingBottom={2}>
              <Link onClick={onClose}>{l(content.cancel, language)}</Link>
            </Box>
          </Grid>
        </Grid>
        {paymentProcessor === 'ach' &&
          amountCharged.currency.toLowerCase() === 'usd' && (
            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontSize: '10px' }}>
                {l(content.mandate, language)}
              </Typography>
            </Grid>
          )}
      </Box>
    </Dialog>
  );
};

const TryAgainButton = ({ tryAgain }) => {
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';
  return (
    <Button
      onClick={tryAgain}
      hoverColor="#555354"
      text={l(content.tryAgain, language)}
      disabledText={l(content.tryAgain, language)}
      data-try-again-payment
    />
  );
};

const ConfirmPaymentButton = ({ paymentConfirmed, isDuplicateACH = false }) => {
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';
  return (
    <Button
      onClick={paymentConfirmed}
      hoverColor="#555354"
      text={
        isDuplicateACH
          ? l(content.confirmAndProcess, language)
          : l(content.confirmPay, language)
      }
      disabledText={
        isDuplicateACH
          ? l(content.confirmAndProcess, language)
          : l(content.confirmPay, language)
      }
      data-confirm-payment
    />
  );
};

const TokenLoadingButton = props => {
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';

  return (
    <Button
      disabled
      hoverColor="#555354"
      text={
        props.paymentProcessor === 'ach'
          ? 'Connecting to Stripe ...'
          : l(content.connectToPayment, language)
      }
      disabledText={
        props.paymentProcessor === 'ach'
          ? l(content.connectToPayment, 'en-ca')
          : l(content.connectToPayment, language)
      }
      data-connect-plaid
    />
  );
};

const PlaidLinkTokenLoader = ({
  orderId,
  onPlaidLinkTokenSucceed,
  onPlaidLinkTokenFailure,
  amount,
}) => {
  useEffect(() => {
    async function fetchLinkToken() {
      try {
        const linkToken = await getLinkToken(orderId);
        localStorage.setItem(
          'plaidOauthObject',
          JSON.stringify({
            linkToken: linkToken,
            amount: amount,
            orderId: orderId,
          })
        );
        onPlaidLinkTokenSucceed(linkToken);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        onPlaidLinkTokenFailure();
      }
    }
    fetchLinkToken();
  }, []);

  return <TokenLoadingButton paymentProcessor={''} />;
};

const CadPadTokenLoader = ({
  orderId,
  amount,
  onTokenSuccess,
  onTokenFailure,
  paymentProcessor,
}) => {
  useEffect(() => {
    let clientSecret = async () => {
      try {
        const token = await createPadPayment(amount, orderId, paymentProcessor);
        onTokenSuccess(token);
      } catch (e) {
        onTokenFailure(true);
      }
    };
    clientSecret();
  }, []);

  return <TokenLoadingButton paymentProcessor={paymentProcessor} />;
};

export const TokenLoaderChooser = ({
  orderId,
  amount,
  onTokenSuccess,
  onTokenFailure,
  paymentProcessor,
}) => {
  switch (amount.currency) {
    case 'USD':
      if (paymentProcessor === 'ach') {
        return (
          <CadPadTokenLoader
            orderId={orderId}
            amount={amount}
            onTokenSuccess={onTokenSuccess}
            onTokenFailure={onTokenFailure}
            paymentProcessor={paymentProcessor}
          />
        );
      } else {
        return (
          <PlaidLinkTokenLoader
            orderId={orderId}
            onPlaidLinkTokenSucceed={onTokenSuccess}
            onPlaidLinkTokenFailure={onTokenFailure}
            amount={amount}
          />
        );
      }
    case 'CAD':
      return (
        <CadPadTokenLoader
          orderId={orderId}
          amount={amount}
          onTokenSuccess={onTokenSuccess}
          onTokenFailure={onTokenFailure}
        />
      );
    default:
      throw new Error('Unsupported');
  }
};
